import { Layout as AntLayout, Menu, Button, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';

const { Header, Sider, Content } = AntLayout;

// External destinations — kept as constants so we never accidentally route to
// them client-side. These open in a new tab via the Menu item's onClick handler.
const DOCS_URL = 'https://docs.zynplex.ca';
const LANDING_URL = 'https://zynplex.ca';

interface LayoutProps {
  children: ReactNode;
}

/**
 * Portal layout. Renders only when the user is signed in (App.tsx switches
 * between this and the AuthShell based on auth state). Sidebar nav points
 * at in-app routes; the "API docs" entry now opens docs.zynplex.ca in a
 * new tab because the portal no longer hosts its own docs page.
 */
export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((u) => {
        if (!cancelled) {
          setEmail(u.signInDetails?.loginId);
        }
      })
      .catch(() => {
        /* layout only mounts when authed; getCurrentUser failure is transient */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // `docs` and `landing` aren't real react-router paths — they're sentinels we
  // handle in the Menu onClick below to open external sites in a new tab.
  const items = [
    { key: '/', label: 'Dashboard' },
    { key: '/feeds', label: 'CREA DDF feeds' },
    { key: '/api-keys', label: 'API keys' },
    { key: '/billing', label: 'Billing' },
    { key: '/usage', label: 'Usage' },
    { key: 'docs', label: 'API docs ↗' },
    { key: 'landing', label: 'Marketing site ↗' },
  ];

  async function onSignOut() {
    try {
      await signOut();
    } finally {
      setEmail(undefined);
      window.location.href = LANDING_URL;
    }
  }

  function handleMenuClick(key: string) {
    if (key === 'docs') {
      window.open(DOCS_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    if (key === 'landing') {
      window.open(LANDING_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(key);
  }

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography.Title
          level={4}
          style={{ color: 'white', margin: 0, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          Zynplex Portal
        </Typography.Title>
        <div style={{ color: 'white' }}>
          {email && <span style={{ marginRight: 16 }}>{email}</span>}
          <Button onClick={onSignOut}>Sign out</Button>
        </div>
      </Header>
      <AntLayout>
        <Sider width={220} theme="light">
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={items}
            onClick={(e) => handleMenuClick(e.key)}
            style={{ height: '100%' }}
          />
        </Sider>
        <Content style={{ padding: 24, background: '#f5f5f5' }}>{children}</Content>
      </AntLayout>
    </AntLayout>
  );
}
